import { createRequestInstance } from '../'

import {
  Affiliate,
  Invitation,
  InvitationStatus,
  Role,
  MyAffiliate,
  AffiliateMemberCertification,
  AffiliateMember,
} from 'types'

export const AFFILIATES_URL = import.meta.env.VITE_AFFILIATES_URL || ''

const request = createRequestInstance({
  baseURL: AFFILIATES_URL,
})

export type FetchMyAffiliateResponse = {
  affiliateId: number
  groupId: number
  affiliateName: string
  affiliateStanding: 'good' | 'bad'
  roles: Role[]
  financialStatus: string
  affiliateCountryCode: string
}[]

export type PatchAffiliateMemberPayload = {
  roles?: string[]
  isPublic?: boolean
  order?: number
}

export async function fetchMyAffiliates(
  userId,
): Promise<MyAffiliate[] | undefined> {
  const response = await request.get<MyAffiliate[]>(
    `/members/${userId}/affiliates`,
    {
      params: {
        include_bad_standing: true,
      },
    },
  )
  const { data } = response

  if (!data || data.length === 0) {
    return
  }

  return data
}

export type FetchAffiliateResponse = {
  id: number
  groupId: number
  name: string
  address: string
  city: string
  stateCode: string
  countryCode: string
  contactEmail: string
  contactPhone: string
  settings: any
  web: string
  bio: string
  lor?: {
    userId: number
    firstName: string
    lastName: string
    email: string
    id: number
    creationDate: string
    lastModified_date?: string
    roundtablesInterested?: boolean
  }
  images?: {
    primary?: {
      url?: string
    }
    gallery?: GalleryImages
    coaches?: {
      id: number
      image?: {
        url?: string
      }
    }[]
  }
}

export type GalleryImages = {
  url?: string
  order?: number
}[]

export async function fetchAffiliate(affiliateId: string): Promise<Affiliate> {
  const response = await request.get<Affiliate>(`/affiliates/${affiliateId}`, {
    params: {
      detail: 'full',
      expand: 'insurance',
    },
  })
  return response.data
}

export enum SearchForTeammateErrorCode {
  IS_LOR = 'is_lor',
  EXPIRED_CERTS = 'expired_certs',
  NO_CERTS = 'no_certs',
  IN_GROUP = 'in_group',
  IN_TEAM = 'in_team',
}

type SearchForTeammateResponse = {
  firstName?: string
  lastName?: string
  userId: number
  certifications?: AffiliateMemberCertification[]
  errorCode?: SearchForTeammateErrorCode
  message?: string
}

export async function searchForTeammate(email: string) {
  const response = await request.get<SearchForTeammateResponse>(
    '/teammate_search()',
    {
      params: {
        role: Role.Coach,
        email,
      },
    },
  )
  const { data } = response

  const { userId, ...rest } = data

  return {
    id: userId,
    email,
    ...rest,
  }
}

type InviteTeammatesBody = {
  userIds: number[]
  affiliateId: number
}

export async function inviteTeammates({
  affiliateId,
  userIds,
}: InviteTeammatesBody) {
  const response = await request.post(
    `/affiliates/${affiliateId}/invitations`,
    {
      users: userIds.map((id) => ({
        id,
        roles: [Role.Coach],
      })),
    },
  )

  return response.data
}

type FetchInvitationsResponse = {
  certifications: AffiliateMemberCertification[]
  roles: [Role]
  userEmail: string
  userId: number
  token: string
  userName: string
}[]

export async function fetchInvitations(
  affiliateId: number,
): Promise<Invitation[]> {
  const response = await request.get<FetchInvitationsResponse>(
    `/affiliates/${affiliateId}/invitations?expand=certifications&status=pending`,
  )

  return response.data.map((invitation) => {
    const { userEmail, userId, userName, roles, certifications, ...rest } =
      invitation

    return {
      email: userEmail,
      name: userName,
      id: userId,
      roles,
      certifications,
      ...rest,
    }
  })
}

export type FetchMembersResponse = AffiliateMember[]

export async function fetchAffiliateMembers(
  affiliateId: number,
  includeExpired = false,
): Promise<FetchMembersResponse> {
  const params = {
    expand: 'users,certifications',
  }
  if (includeExpired) {
    params['include_expired'] = true
  }
  const response = await request.get<FetchMembersResponse>(
    `/affiliates/${affiliateId}/members`,
    { params },
  )

  return response.data
}

type FetchInvitationResponse = {
  roles: [Role]
  userEmail: string
  userId: number
  userName: string
}

export async function fetchInvitation(
  affiliateId: number,
  token: string,
): Promise<Invitation> {
  const response = await request.get<FetchInvitationResponse>(
    `/affiliates/${affiliateId}/invitations/${token}`,
  )
  const { userEmail, userId, userName, roles, ...rest } = response.data

  return {
    certifications: [],
    token,
    email: userEmail,
    name: userName,
    id: userId,
    roles,
    ...rest,
  }
}

export async function respondToInvitation({
  affiliateId,
  token,
  accept,
}: {
  affiliateId: number
  token: string
  accept: boolean
}) {
  const response = await request.post(
    `/affiliates/${affiliateId}/invitations/${token}`,
    {
      accept,
    },
  )

  return response.data
}

export async function assignManager({ affiliateId, userId }) {
  const response = await request.patch(
    `/affiliates/${affiliateId}/members/${userId}`,
    {
      roles: [Role.Manager],
    },
  )

  return response.data
}

export async function revertToCoach({ affiliateId, userId }) {
  const response = await request.patch(
    `/affiliates/${affiliateId}/members/${userId}`,
    {
      roles: [Role.Coach],
    },
  )

  return response.data
}

type LOR = {
  roundtablesInterested: boolean
}

export async function fetchLOR(userId: number) {
  const response = await request.get<LOR>(`/lors/${userId}`)

  return response.data
}

export async function removeTeammate({ affiliateId, userId }) {
  const response = await request.delete(
    `/affiliates/${affiliateId}/members/${userId}`,
  )

  return response.data
}

type ResendInviteParams = {
  affiliateId: number
  token: string
}

export async function resendInvite({ affiliateId, token }: ResendInviteParams) {
  const response = await request.post(
    `/affiliates/${affiliateId}/invitations/${token}/resend()`,
  )

  return response.data
}

type RevokeInviteParams = {
  affiliateId: number
  token: string
}

export async function revokeInvite({ affiliateId, token }: RevokeInviteParams) {
  const response = await request.patch(
    `/affiliates/${affiliateId}/invitations/${token}`,
    {
      status: InvitationStatus.Revoked,
    },
  )

  return response.data
}

export type FetchMyApplicationResponse = {
  application: string
  activation: string
  insurance: string
  license: string
  location: string
  payment: string
  userId: number
}

export async function fetchMyApplication() {
  const response = await request.get<FetchMyApplicationResponse>(
    `/applications/me`,
  )
  return response.data
}

export async function patchAffiliate(affiliateId: number, updateFields: any) {
  return await request.patch(`/affiliates/${affiliateId}`, {
    ...updateFields,
  })
}

export async function patchAffiliateMember(
  affiliateId: number,
  memberUserId: number,
  updateFields: PatchAffiliateMemberPayload,
) {
  return await request.patch(
    `affiliates/${affiliateId}/members/${memberUserId}`,
    {
      ...updateFields,
    },
  )
}

export async function patchAffiliateMembersOrdering(
  affiliateId: number,
  coaches: { id: number; order: number }[],
) {
  return await request.patch(`affiliates/${affiliateId}/members/order`, coaches)
}

export async function patchAffiliateGalleryImagesOrdering(
  affiliateId?: number,
  images?: { url?: string; order?: number }[],
) {
  const galleryImages = images.map((image) => ({
    image: image.url.substring(image.url.lastIndexOf('/') + 1),
    order: image.order,
  }))
  return await request.patch(
    `affiliates/${affiliateId}/images/order`,
    galleryImages,
  )
}
